/**
 * serverSelector.js — 多节点自动探活与切换
 *
 * 逻辑：
 *  1. 主节点 = VITE_API_BASE_URL（环境变量注入）
 *  2. 备用节点 = config.fallbackNodes（gameapi1~100.fksgame.com）
 *  3. 请求/WS 连续失败 FAIL_THRESHOLD 次 → 探活所有节点 → 切换到第一个可用的
 *  4. 切换后每 60 秒探活一次主节点，主节点恢复则切回
 */

import config from '@/config'

const PROBE_TIMEOUT  = 3000   // 探活超时 ms
const FAIL_THRESHOLD = 2      // 连续失败多少次触发切换
const RESTORE_INTERVAL = 60000 // 主节点恢复探测间隔 ms

// 全部节点：主节点 + 备用节点
const _allNodes = [
  { api: config.apiBaseUrl, ws: config.wsBaseUrl },
  ...config.fallbackNodes
]

// 当前激活节点下标（0 = 主节点）
let _curIdx = 0
// 各节点连续失败计数
const _failCount = new Map()
// 是否正在切换（防并发）
let _switching = false
// 后台恢复探测定时器
let _restoreTimer = null

/** 获取当前激活节点 { api, ws } */
export function getCurrentNode() {
  return _allNodes[_curIdx]
}

/** 请求/WS 成功后调用，重置当前节点失败计数 */
export function reportSuccess() {
  _failCount.set(_curIdx, 0)
}

/**
 * 请求/WS 失败后调用
 * 达到阈值后自动切换到下一个可用节点
 */
export async function reportFailure() {
  const cnt = (_failCount.get(_curIdx) || 0) + 1
  _failCount.set(_curIdx, cnt)
  console.warn(`[ServerSelector] 节点[${_curIdx}] ${_allNodes[_curIdx].api} 失败 ${cnt} 次`)

  if (cnt >= FAIL_THRESHOLD && !_switching) {
    await _doSwitch()
  }
}

/** 探活单个节点，返回 true/false */
async function _probe(node) {
  try {
    const ctrl = new AbortController()
    const tid  = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT)
    const res  = await fetch(`${node.api}/health`, {
      signal: ctrl.signal,
      cache: 'no-store'
    })
    clearTimeout(tid)
    return res.ok
  } catch {
    return false
  }
}

/** 执行切换：从当前节点之后依次探活，找到第一个通的 */
async function _doSwitch() {
  _switching = true
  const total = _allNodes.length
  console.warn(`[ServerSelector] 开始探活，当前节点[${_curIdx}]，共 ${total} 个节点`)

  for (let i = 1; i < total; i++) {
    const idx  = (_curIdx + i) % total
    const node = _allNodes[idx]
    const ok   = await _probe(node)
    console.log(`[ServerSelector] 探活节点[${idx}] ${node.api} → ${ok ? '✓' : '✗'}`)
    if (ok) {
      _applySwitch(idx)
      _switching = false
      return
    }
  }

  // 全部不通，保持当前节点，等下次触发
  console.error('[ServerSelector] 所有节点不可用，保持当前节点')
  _failCount.set(_curIdx, 0) // 重置计数，避免无限循环
  _switching = false
}

/** 应用节点切换，更新 config 并广播事件 */
function _applySwitch(newIdx) {
  const prev = _curIdx
  _curIdx    = newIdx
  _failCount.set(newIdx, 0)

  const node = _allNodes[newIdx]
  // 同步更新 config，使后续 request 拦截器直接读到新地址
  config.apiBaseUrl = node.api
  config.wsBaseUrl  = node.ws

  console.warn(`[ServerSelector] ✅ 切换成功: [${prev}] → [${newIdx}]  ${node.api}`)

  // 通知 socket.js 用新地址重连
  window.dispatchEvent(new CustomEvent('server:switched', {
    detail: { prevIndex: prev, newIndex: newIdx, node }
  }))

  // 启动后台定时探活主节点，恢复后切回
  _startRestoreProbe()
}

/** 后台定时探活主节点，恢复后切回 */
function _startRestoreProbe() {
  if (_restoreTimer) return
  _restoreTimer = setInterval(async () => {
    if (_curIdx === 0) {
      clearInterval(_restoreTimer)
      _restoreTimer = null
      return
    }
    const ok = await _probe(_allNodes[0])
    if (ok) {
      console.log('[ServerSelector] 主节点已恢复，切回主节点')
      clearInterval(_restoreTimer)
      _restoreTimer = null
      _applySwitch(0)
    }
  }, RESTORE_INTERVAL)
}
